import { useCallback, useEffect, useRef, useState } from "react";

export type AnalysisTableKey = "fields" | "variables" | "dimensions" | "measures" | "objects";

const SORT_STORAGE_KEY = "senseguard:analysis-sort";
const FILTERS_STORAGE_PREFIX = "senseguard:analysis-filters:";

type SortState<Col extends string> = {
  sortBy: Col;
  sortDir: "asc" | "desc";
};

function readJson<T>(key: string): T | undefined {
  if (typeof window === "undefined") return undefined;
  const raw = window.localStorage.getItem(key);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, JSON.stringify(value));
}

function readSort<Col extends string>(
  tableKey: AnalysisTableKey,
  defaults: SortState<Col>,
): SortState<Col> {
  const stored = readJson<Record<string, SortState<string>>>(SORT_STORAGE_KEY);
  const entry = stored?.[tableKey];
  if (!entry) return defaults;
  return { sortBy: entry.sortBy as Col, sortDir: entry.sortDir };
}

function readFilters<F>(analysisId: string, tableKey: AnalysisTableKey, defaults: F): F {
  const stored = readJson<Record<string, unknown>>(FILTERS_STORAGE_PREFIX + analysisId);
  const entry = stored?.[tableKey];
  if (!entry || typeof entry !== "object") return defaults;
  return { ...defaults, ...(entry as F) };
}

export function useAnalysisTableSort<Col extends string>(
  tableKey: AnalysisTableKey,
  defaults: SortState<Col>,
) {
  const [state, setState] = useState<SortState<Col>>(() => readSort(tableKey, defaults));

  const setSort = useCallback(
    (sortBy: Col, sortDir: "asc" | "desc") => {
      setState({ sortBy, sortDir });
      const stored = readJson<Record<string, SortState<string>>>(SORT_STORAGE_KEY) ?? {};
      stored[tableKey] = { sortBy, sortDir };
      writeJson(SORT_STORAGE_KEY, stored);
    },
    [tableKey],
  );

  return { sortBy: state.sortBy, sortDir: state.sortDir, setSort };
}

export function useAnalysisTableFilters<F extends object>(
  analysisId: string,
  tableKey: AnalysisTableKey,
  defaults: F,
) {
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  const [filters, setFiltersState] = useState<F>(() => readFilters(analysisId, tableKey, defaults));

  useEffect(() => {
    setFiltersState(readFilters(analysisId, tableKey, defaultsRef.current));
  }, [analysisId, tableKey]);

  const setFilters = useCallback(
    (patch: Partial<F>) => {
      setFiltersState((prev) => {
        const next = { ...prev, ...patch };
        const key = FILTERS_STORAGE_PREFIX + analysisId;
        const stored = readJson<Record<string, unknown>>(key) ?? {};
        stored[tableKey] = next;
        writeJson(key, stored);
        return next;
      });
    },
    [analysisId, tableKey],
  );

  return { filters, setFilters };
}
